import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
import SearchBar from "../../../SearchBarTable/SearchBarTable";
import Dropdown from "../../../DropDowns/DropDown";
import { FaKey } from "react-icons/fa";
// @ts-ignore CSS is loaded by the application bundler.
import "../../AgGridTheme.css";
import SortHeader from "../../SortingHeader";
import {
  ClientSideRowModelModule,
  ModuleRegistry,
  TextFilterModule,
  PaginationModule,
} from "ag-grid-community";
import { filterPermissionObjects } from "./permissionObjectUtils";

ModuleRegistry.registerModules([ClientSideRowModelModule, TextFilterModule, PaginationModule]);

interface PermissionObjectTableProps {
  permissionObjects: any[];
  services: string[];
  selectedRow: number | null;
  onRowSelect: (po: any) => void;
}

const headerClass = "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer";

const col = (field: string, headerName: string, minWidth: number) => ({
  headerName,
  field,
  sortable: true,
  flex: 1,
  minWidth,
  headerComponent: SortHeader,
  headerComponentParams: {
    displayName: headerName,
    enableSorting: true,
    icon: <FaKey className="text-gray-600" />,
  },
  headerClass,
});

const filterClass = "p-1 border border-gray-300 rounded-lg text-sm bg-white";
const TYPE_FILTER = [
  { value: "", label: "All types" },
  { value: "API", label: "API" },
  { value: "UI_ELEMENT", label: "UI_ELEMENT" },
  { value: "BLOB_FOLDER", label: "BLOB_FOLDER" },
];
const ENABLED_FILTER = [
  { value: "", label: "Enabled and disabled" },
  { value: "Y", label: "Enabled" },
  { value: "N", label: "Disabled" },
];

const PermissionObjectTable: React.FC<PermissionObjectTableProps> = ({
  permissionObjects,
  services,
  selectedRow,
  onRowSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [type, setType] = useState("");
  const [service, setService] = useState("");
  const [enabled, setEnabled] = useState("");
  const gridRef = useRef<AgGridReact>(null);

  const rows = useMemo(
    () =>
      filterPermissionObjects(permissionObjects, {
        types: type ? [type] : undefined,
        service,
        enabled,
        query: searchQuery,
      }),
    [permissionObjects, type, service, enabled, searchQuery]
  );

  const columnDefs = useMemo(
    () => [
      col("permission_object_name", "Name", 220),
      col("po_type_code", "Type", 110),
      col("po_path", "Path", 220),
      col("service_name", "Service", 120),
      col("enabled", "Enabled", 90),
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      cellStyle: {
        padding: "8px 12px",
        fontSize: "16px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
      },
    }),
    []
  );

  const handleRowClicked = useCallback((event: any) => onRowSelect(event.data), [onRowSelect]);

  return (
    <div className="p-4 bg-white shadow-md rounded-xl font-poppins">
      {/* One row at desktop width: a narrower search pill, the three filters beside it. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full w-60 min-w-0 [&>div]:w-full [&_input]:w-full [&_input]:min-w-0">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Search name or path..."
          />
        </div>
        <Dropdown
          ariaLabel="Type filter"
          options={TYPE_FILTER}
          selectedValue={type}
          onValueChange={setType}
          disabled={false}
          placeholder="All types"
          className={`${filterClass} w-40`}
        />
        <Dropdown
          ariaLabel="Service filter"
          options={[{ value: "", label: "All services" }, ...services.map((s) => ({ value: s, label: s }))]}
          selectedValue={service}
          onValueChange={setService}
          disabled={false}
          placeholder="All services"
          showOptionTooltips
          className={`${filterClass} w-56`}
        />
        <Dropdown
          ariaLabel="Enabled filter"
          options={ENABLED_FILTER}
          selectedValue={enabled}
          onValueChange={setEnabled}
          disabled={false}
          placeholder="Enabled and disabled"
          className={`${filterClass} w-56`}
        />
      </div>

      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700"
        style={{
          height: "var(--admin-tab-grid-height, max(475px, calc(100dvh - 320px)))",
          width: "100%",
          overflow: "auto",
          border: "1px solid #E7E4FF",
          borderRadius: "8px",
        }}
      >
        <AgGridReact
          ref={gridRef}
          theme="legacy"
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection="single"
          rowHeight={60}
          headerHeight={55}
          pagination={true}
          paginationPageSize={100}
          animateRows
          onRowClicked={handleRowClicked}
          domLayout="normal"
          getRowClass={(params) =>
            params.data.permission_object_id === selectedRow ? "bg-gray-200" : ""
          }
        />
      </div>
    </div>
  );
};

export default PermissionObjectTable;
